/**
 * Modal Container Component
 * 
 * Provides a render-prop pattern for modals to access their state and data.
 * This eliminates the need for separate container/wrapper components.
 * 
 * Usage:
 * ```typescript
 * <ModalContainer<CreateDealModalData> modalId={MODAL_IDS.deals.create}>
 *   {({ data, isOpen, onClose }) => (
 *     <ResponsiveModal
 *       globalModalId={MODAL_IDS.deals.create}
 *       open={isOpen}
 *       onOpenChange={(open) => !open && onClose()}
 *       title="Create Deal"
 *     >
 *       Modal content
 *     </ResponsiveModal>
 *   )}
 * </ModalContainer>
 * ```
 */

import React, { useMemo } from 'react';
import { useGlobalModalStack } from './context.js';

export interface ModalContainerProps<TData = any> {
  modalId: string;
  children: (props: {
    data: TData | undefined;
    isOpen: boolean;
    onClose: () => void;
  }) => React.ReactNode;
}

/**
 * Container component that provides modal state and data to children
 * 
 * This component handles:
 * - Checking if the modal is open
 * - Retrieving modal data
 * - Providing a close callback
 * 
 * Benefits:
 * - Eliminates need for separate container components
 * - Type-safe data access (when used with TypeScript)
 * - Consistent pattern across all modals
 * - Reduces boilerplate
 */
export function ModalContainer<TData = any>({
  modalId,
  children,
}: ModalContainerProps<TData>) {
  const { isModalOpen, closeModal, getModalData, modalDataRevision } =
    useGlobalModalStack();
  const isOpen = isModalOpen(modalId);
  const data = useMemo(
    () => getModalData(modalId) as TData | undefined,
    [getModalData, modalId, modalDataRevision, isOpen],
  );

  return (
    <>
      {children({
        data,
        isOpen,
        onClose: () => closeModal(modalId),
      })}
    </>
  );
}
